import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Grid,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Company, CreateCompanyRequest, UpdateCompanyRequest } from '@/constants/types';

interface CompanyDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCompanyRequest | UpdateCompanyRequest) => void;
  company?: Company | null;
  isLoading?: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Company name is required'),
  description: Yup.string().required('Description is required'),
  addressLine1: Yup.string().required('Address line 1 is required'),
  addressLine2: Yup.string(),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  pincode: Yup.string()
    .required('Pincode is required')
    .matches(/^\d{6}$/, 'Pincode must be 6 digits'),
  gst: Yup.string().required('GST number is required'),
  email: Yup.string()
    .email('Please enter a valid email')
    .required('Email is required'),
  mobile: Yup.string()
    .required('Mobile number is required')
    .matches(/^\d{10}$/, 'Mobile number must be 10 digits'),
  isActive: Yup.boolean(),
});

export const CompanyDialog: React.FC<CompanyDialogProps> = ({
  open,
  onClose,
  onSubmit,
  company,
  isLoading = false,
}) => {
  const isEditMode = !!company;

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      gst: '',
      email: '',
      mobile: '',
      isActive: true,
    },
    validationSchema,
    onSubmit: (values) => {
      if (isEditMode) {
        onSubmit(values as UpdateCompanyRequest);
      } else {
        const { isActive, ...createData } = values;
        onSubmit(createData as CreateCompanyRequest);
      }
    },
  });

  useEffect(() => {
    if (open) {
      if (company) {
        formik.setValues({
          name: company.name,
          description: company.description,
          addressLine1: company.addressLine1,
          addressLine2: company.addressLine2 || '',
          city: company.city,
          state: company.state,
          pincode: company.pincode,
          gst: company.gst,
          email: company.email,
          mobile: company.mobile,
          isActive: company.isActive,
        });
      } else {
        formik.resetForm();
      }
    }
  }, [open, company]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isEditMode ? 'Edit Company' : 'Add Company'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Grid container spacing={2}>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  name="name"
                  label="Company Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  required
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  name="email"
                  label="Email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  required
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              name="description"
              label="Description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              multiline
              rows={3}
              required
            />

            <Grid container spacing={2}>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  name="addressLine1"
                  label="Address Line 1"
                  value={formik.values.addressLine1}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
                  helperText={formik.touched.addressLine1 && formik.errors.addressLine1}
                  required
                />
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  name="addressLine2"
                  label="Address Line 2 (Optional)"
                  value={formik.values.addressLine2}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.addressLine2 && Boolean(formik.errors.addressLine2)}
                  helperText={formik.touched.addressLine2 && formik.errors.addressLine2}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{xs: 12, sm: 4}}>
                <TextField
                  fullWidth
                  name="city"
                  label="City"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  required
                />
              </Grid>
              <Grid size={{xs: 12, sm: 4}}>
                <TextField
                  fullWidth
                  name="state"
                  label="State"
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  required
                />
              </Grid>
              <Grid size={{xs: 12, sm: 4}}>
                <TextField
                  fullWidth
                  name="pincode"
                  label="Pincode"
                  value={formik.values.pincode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                  helperText={formik.touched.pincode && formik.errors.pincode}
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  name="gst"
                  label="GST Number"
                  value={formik.values.gst}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.gst && Boolean(formik.errors.gst)}
                  helperText={formik.touched.gst && formik.errors.gst}
                  required
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <TextField
                  fullWidth
                  name="mobile"
                  label="Mobile Number"
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                  helperText={formik.touched.mobile && formik.errors.mobile}
                  required
                />
              </Grid>
            </Grid>

            {isEditMode && (
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formik.values.isActive}
                    onChange={formik.handleChange}
                  />
                }
                label="Active"
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};